import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import type { XkEvent, XkEventGroup, XkStation } from '@/lib/api/types';

export interface UserFavorite {
  id: number;
  userId: number;
  name: string;
  path: string;
  createdAt: string;
  updatedAt: string;
}

interface ListEnvelope {
  data: { items: UserFavorite[]; total: number };
}

export const favoritesApi = {
  list: () => api.get<ListEnvelope>('/favorites'),
  create: (body: { name: string; path: string }) =>
    api.post<UserFavorite>('/favorites', body),
  remove: (id: number) => api.delete(`/favorites/${id}`),
};

export function useFavorites() {
  return useQuery({
    queryKey: queryKeys.favorites.all,
    queryFn: async () => {
      const res = await favoritesApi.list();
      return res.data.items;
    },
    staleTime: 30_000,
  });
}

export function useAddFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: favoritesApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.favorites.all }),
  });
}

export function useRemoveFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: favoritesApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.favorites.all }),
  });
}

/**
 * Brand-scoped view of the user's favorites.
 *
 * The `/favorites` backend stores `{name, path}` rows without any brand
 * metadata, so scoping is enforced on the client by inspecting the path:
 *
 *   /brand/N            → kept iff N === selectedBrandId
 *   /projects/N…        → kept iff N is in the brand's event groups
 *   /projects/N/events/M → kept iff N AND M belong to the brand
 *   /stations/N         → kept iff N belongs to the brand
 *   anything else        → kept (treated as global, e.g. /profile, /settings)
 *
 * The supporting queries (`eventGroups.byBrand`, `events.byBrand`,
 * `stations.byBrand`) are the same React Query keys used by the brand
 * profile + sidebar tree, so cache hits are the common case.
 */
export function useBrandScopedFavorites() {
  const brandId = useSelectedBrandId();
  const favoritesQ = useFavorites();

  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId ?? 0),
    queryFn: () =>
      api.list<XkEventGroup>('/xk-event-group', {
        filter: { companyId: brandId },
      }),
    enabled: !!brandId,
    staleTime: 60_000,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId ?? 0),
    queryFn: () =>
      api.list<XkEvent>('/xk-event', { filter: { companyId: brandId } }),
    enabled: !!brandId,
    staleTime: 60_000,
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byBrand(brandId ?? 0),
    queryFn: () =>
      api.list<XkStation>('/xk-station', { filter: { companyId: brandId } }),
    enabled: !!brandId,
    staleTime: 60_000,
  });

  const data = useMemo(() => {
    const all = favoritesQ.data ?? [];
    if (!brandId) return all;
    const projectIds = new Set((groupsQ.data?.items ?? []).map((g) => g.id));
    const eventIds = new Set((eventsQ.data?.items ?? []).map((e) => e.id));
    const stationIds = new Set((stationsQ.data?.items ?? []).map((s) => s.id));
    return all.filter((f) => {
      const path = f.path;
      const brandMatch = path.match(/^\/brand\/(\d+)/);
      if (brandMatch) return Number(brandMatch[1]) === brandId;
      const projectMatch = path.match(/^\/projects\/(\d+)(?:\/events\/(\d+))?/);
      if (projectMatch) {
        const pid = Number(projectMatch[1]);
        const eid = projectMatch[2] ? Number(projectMatch[2]) : null;
        if (!projectIds.has(pid)) return false;
        if (eid != null && !eventIds.has(eid)) return false;
        return true;
      }
      const stationMatch = path.match(/^\/stations\/(\d+)/);
      if (stationMatch) return stationIds.has(Number(stationMatch[1]));
      return true;
    });
  }, [
    favoritesQ.data,
    brandId,
    groupsQ.data,
    eventsQ.data,
    stationsQ.data,
  ]);

  return {
    data,
    isLoading: favoritesQ.isLoading,
  };
}
